import React, { useState } from 'react';
import {
  Table,
  Badge,
  Spinner,
  Alert,
  CardHeader,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import {
  FaAndroid,
  FaApple,
  FaDesktop,
  FaQuestionCircle,
  FaAmazon,
  FaTv
} from 'react-icons/fa';
import {
  SiLg,
  SiSamsung,
  SiRoku
} from 'react-icons/si';
import { toast } from 'react-toastify';

import {
  useGetDeviceInfoQuery,
  useAddActionNoteMutation,
  useResetDeviceOneMutation
} from '../../../../store/api/userManagementApi.js';
import '../styles.css'
import AddCommentModal from '../shared/add_note.model.jsx';
import { useSelector } from 'react-redux';

const DeviceList = () => {
  console.log("Rendering DeviceList");
  const [modal, setModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [pendingResetDevice, setPendingResetDevice] = useState(null);
  const userData = useSelector(state => state.userManagement.userData) || {};
  const user_id = userData?.user_id;

  const {
    data: deviceResponse,
    isLoading: loading,
    error: queryError,
    refetch
  } = useGetDeviceInfoQuery(user_id, { skip: !user_id });

  const [addActionNote] = useAddActionNoteMutation();
  const [resetDeviceOne] = useResetDeviceOneMutation();

  const devices = deviceResponse?.data || [];
  const error =
    deviceResponse?.success === false
      ? (deviceResponse?.message || 'Failed to fetch device data')
      : (queryError?.data || null);

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'android':
        return <FaAndroid size={20} className="text-success" />;
      case 'ios':
        return <FaApple size={20} className="text-secondary" />;
      case 'roku':
        return <SiRoku size={20} className="text-primary" />;
      case 'fire tv':
        return <FaAmazon size={20} className="text-warning" />;
      case 'webos':
      case 'webos(lg tv)':
        return <SiLg size={20} className="text-danger" />;
      case 'tizen':
      case 'tizen(samsung tv)':
        return <SiSamsung size={20} className="text-info" />;
      case 'android tv':
        return <FaTv size={20} className="text-success" />;
      case 'website':
        return <FaDesktop size={20} className="text-primary" />;
      case 'apple tv':
        return <FaTv size={20} className="text-secondary" />;
      case 'vizio':
        return <FaTv size={20} className="text-info" />;
      default:
        return <FaQuestionCircle size={20} className="text-secondary" />;
    }
  };

  const getDeviceTypeBadge = (deviceType) => {
    const type = deviceType?.toLowerCase();
    let color = 'secondary';

    if (type?.includes('android')) color = 'success';
    if (type?.includes('ios') || type?.includes('apple')) color = 'secondary';
    if (type?.includes('roku')) color = '#0091ae';
    if (type?.includes('fire')) color = 'warning';
    if (type?.includes('webos') || type?.includes('lg')) color = 'danger';
    if (type?.includes('tizen') || type?.includes('samsung')) color = 'info';
    if (type?.includes('website')) color = '#0091ae';
    if (type?.includes('vizio')) color = 'info';

    return (
      <Badge color={color} className="text-capitalize">
        {deviceType || 'Unknown Device'}
      </Badge>
    );
  };

  const handleResetClick = (device) => {
    setSelectedDevice(device);
    setModal(true);
  };

  const handleConfirmReset = async () => {
    setModal(false);
    if (!selectedDevice) return;

    // Store device and show note modal
    setPendingResetDevice(selectedDevice);
    setIsCommentModalOpen(true);
    setSelectedDevice(null);
  };

  const handleCommentSubmit = async (note) => {
    try {
      const commentData = {
        action_type: 'device-reset',
        note: note,
        user_id: user_id
      };

      // Submit note (success or failure doesn't matter for main flow)
      await addActionNote(commentData).unwrap();

      // Close note modal and proceed with device reset
      setIsCommentModalOpen(false);
      if (pendingResetDevice) {
        proceedWithDeviceReset(pendingResetDevice);
      }
    } catch (error) {
      console.error('Comment submission error:', error);
      // Still proceed with device reset even if note fails
      setIsCommentModalOpen(false);
      if (pendingResetDevice) {
        proceedWithDeviceReset(pendingResetDevice);
      }
    }
  };

  const proceedWithDeviceReset = async (device) => {
    toast.info('Resetting device...');
    try {
      const response = await resetDeviceOne({
        userId: user_id,
        deviceId: device.device_id
      }).unwrap();
      if (response.success) {
        toast.success('Device reset successfully!');
        refetch();
      } else {
        toast.error('Failed to reset device.');
      }
    } catch (err) {
      toast.error('Error resetting device.');
    } finally {
      setPendingResetDevice(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner color="primary" />
        <p className="mt-2 text-muted">Loading device information...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="danger" className="m-3">
        {typeof error === 'string' ? error : 'Failed to fetch device data'}
      </Alert>
    );
  }

  return (
    <>
      <CardHeader className="bg-transparent border-bottom">
        <h4 className="mb-0">Connected Devices</h4>
        <p className="text-muted small mb-0">
          List of devices used to access the account
        </p>
      </CardHeader>
      <CardBody>
        {devices.length === 0 ? (
          <div className="text-center p-4">
            <FaQuestionCircle size={40} className="text-muted mb-3" />
            <p className="mb-0">No devices found</p>
          </div>
        ) : (
          <Table responsive hover className="align-middle">
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Device Type</th>
                <th>IP Address</th>
                <th>Country</th>
                <th>City</th>
                <th>Last Used</th>
                <th style={{ paddingLeft: '25px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device, index) => (
                <tr key={index}>
                  <td>
                    {getDeviceIcon(device.device_type)}
                  </td>
                  <td>
                    {getDeviceTypeBadge(device.device_type)}
                  </td>
                  <td>
                    {device.ipaddress ||
                      <span className="text-muted">Not available</span>
                    }
                  </td>
                  <td>
                    <div style={{ maxWidth: '300px' }} className="text-truncate">
                      {device.country ||
                        <span className="text-muted">Not available</span>
                      }
                    </div>
                  </td>
                  <td>
                    <div style={{ maxWidth: '300px' }} className="text-truncate">
                      {device.city ||
                        <span className="text-muted">Not available</span>
                      }
                    </div>
                  </td>
                  <td>
                    {device.last_used ||
                      <span className="text-muted">Never</span>
                    }
                  </td>
                  <td>
                    <Button color="primary" className='btn btn-primary' onClick={() => handleResetClick(device)}>
                      Reset
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </CardBody>
      <Modal isOpen={modal} toggle={() => setModal(!modal)}>
        <ModalHeader toggle={() => setModal(false)}>Reset Device</ModalHeader>
        <ModalBody>
          Are you sure you want to reset this device?
        </ModalBody>
        <ModalFooter>
          <Button color="btn btn-secondary" onClick={() => setModal(false)}>Cancel</Button>
          <Button color="btn btn-primary" onClick={handleConfirmReset}>Confirm</Button>
        </ModalFooter>
      </Modal>

      <AddCommentModal
        isOpen={isCommentModalOpen}
        toggle={() => setIsCommentModalOpen(false)}
        onSubmit={handleCommentSubmit}
        actionType="device-reset"
      />
    </>
  );
};

export default DeviceList;